import React, { useEffect, useState } from 'react';
import {
  ArrowDown, ArrowUp, BadgeCheck, ChevronDown, CreditCard, FileText, Gavel,
  Handshake, Headphones, Image as ImageIcon, Link as LinkIcon, Loader2, Lock,
  PanelBottom, Plus, Files, Save, Trash2, Trophy, Upload, EyeOff,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import client, { resolveApiAssetUrl } from '../lib/api';
import { BOTTOM_NAV_ICON_OPTIONS } from '../lib/bottomNav';
import { notifySettingsUpdated } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { AdminError, AdminSkeleton } from './components/PageState';
import { HIDDEN_LINK_TEXT, countHiddenLinks, hiddenLinkReason } from './lib/footerLinks';

const iconOptions = [
  { value: 'file-text', label: 'Belge', Icon: FileText },
  { value: 'gavel', label: 'Kurallar', Icon: Gavel },
  { value: 'headphones', label: 'Yardım', Icon: Headphones },
  { value: 'trophy', label: 'Etkinlik', Icon: Trophy },
  { value: 'link', label: 'Bağlantı', Icon: LinkIcon },
];

const move = (items, from, to) => {
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// Bölümler uzun olduğu için katlanır; hangisinin açık kaldığı tarayıcıda
// saklanır ki yönetici her girişte aynı bölümü yeniden açmak zorunda kalmasın.
const SECTIONS_KEY = 'bhs.footer-management.sections';

const readOpenSections = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SECTIONS_KEY) || '{}');
    return {
      menus: !!stored.menus,
      payments: !!stored.payments,
      sponsors: !!stored.sponsors,
      licenses: !!stored.licenses,
    };
  } catch {
    return {
      menus: false, payments: false, sponsors: false, licenses: false,
    };
  }
};

// Ayar kaydından yalnızca bu sayfanın yönettiği alanlar alınır.
const pickFooterFields = (settings) => ({
  footer_menu_groups: settings.footer_menu_groups || [],
  footer_payment_title: settings.footer_payment_title || '',
  footer_payment_methods: settings.footer_payment_methods || [],
  footer_sponsor_title: settings.footer_sponsor_title || '',
  footer_sponsors: settings.footer_sponsors || [],
  footer_licenses: settings.footer_licenses || [],
  footer_license_text: settings.footer_license_text || '',
  // Telif satırındaki başlangıç yılı. Boş bırakılırsa yalnızca içinde
  // bulunulan yıl yazılır (bkz. ParisFooter).
  footer_start_year: settings.footer_start_year ? String(settings.footer_start_year) : '',
});

const FooterManagement = () => {
  const [data, setData] = useState(null);
  const [siteName, setSiteName] = useState('');
  const [busy, setBusy] = useState(false);
  // 'licenses:0' / 'payments:2' gibi anahtar; iki listede aynı sıra numarası
  // birbirinin yükleme göstergesini yakmasın diye alan adıyla birlikte tutulur.
  const [uploadingKey, setUploadingKey] = useState(null);
  const [openSections, setOpenSections] = useState(readOpenSections);
  const [error, setError] = useState('');
  // Footer sayfaları: menüde yayında olmayan sayfaya giden bağlantıyı
  // işaretlemek için (bkz. lib/footerLinks.js). Okunamazsa `null` kalır ve
  // uyarı çizilmez; ekranın asıl işi bu listeye bağlı değildir.
  const [pages, setPages] = useState(null);
  const { toast } = useToast();

  const toggleSection = (key) => setOpenSections((current) => {
    const next = { ...current, [key]: !current[key] };
    try {
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(next));
    } catch {
      /* depolama kapalıysa yalnızca kalıcılık kaybolur */
    }
    return next;
  });

  const load = async () => {
    setError('');
    try {
      const { data: settings } = await client.get('/admin/settings');
      setSiteName(settings.site_name || 'Site');
      setData(pickFooterFields(settings));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Footer ayarları yüklenemedi');
    }
    try {
      const { data: pageList } = await client.get('/admin/footer-pages');
      setPages(Array.isArray(pageList?.items) ? pageList.items : null);
    } catch {
      setPages(null);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      // Yıl alanı formda metindir; boş bırakıldığında `""` gönderilirse uç 422
      // döner. Boş = "başlangıç yılı yok" demektir, o da 0 ile ifade edilir.
      const payload = {
        ...data,
        footer_start_year: Number.parseInt(data.footer_start_year, 10) || 0,
      };
      const { data: settings } = await client.patch('/admin/settings', payload);
      setData(pickFooterFields(settings));
      notifySettingsUpdated();
      toast({ title: 'Footer kaydedildi' });
    } catch (requestError) {
      toast({
        title: 'Hata',
        description: requestError.response?.data?.detail || 'Footer kaydedilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  const setGroups = (footer_menu_groups) => setData({ ...data, footer_menu_groups });
  const setLicenses = (footer_licenses) => setData({ ...data, footer_licenses });
  const setPayments = (footer_payment_methods) => setData({ ...data, footer_payment_methods });
  const setSponsors = (footer_sponsors) => setData({ ...data, footer_sponsors });

  const updateGroup = (groupIndex, patch) => {
    const groups = [...data.footer_menu_groups];
    groups[groupIndex] = { ...groups[groupIndex], ...patch };
    setGroups(groups);
  };

  const updateLink = (groupIndex, linkIndex, patch) => {
    const groups = [...data.footer_menu_groups];
    const links = [...(groups[groupIndex].links || [])];
    links[linkIndex] = { ...links[linkIndex], ...patch };
    groups[groupIndex] = { ...groups[groupIndex], links };
    setGroups(groups);
  };

  const updateLicense = (index, patch) => {
    const licenses = [...data.footer_licenses];
    licenses[index] = { ...licenses[index], ...patch };
    setLicenses(licenses);
  };

  const updatePayment = (index, patch) => {
    const methods = [...data.footer_payment_methods];
    methods[index] = { ...methods[index], ...patch };
    setPayments(methods);
  };

  const updateSponsor = (index, patch) => {
    const sponsors = [...data.footer_sponsors];
    sponsors[index] = { ...sponsors[index], ...patch };
    setSponsors(sponsors);
  };

  // Lisans, sponsor ve yatırım yöntemi görselleri aynı yükleme ucunu kullanır;
  // hedef liste `field` ile belirlenir.
  const uploadImage = async (field, index, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingKey(`${field}:${index}`);
    try {
      const { data: uploaded } = await client.post('/admin/uploads/images', formData);
      setData((current) => {
        const items = [...current[field]];
        items[index] = { ...items[index], image_url: uploaded.url };
        return { ...current, [field]: items };
      });
      toast({
        title: 'Görsel yüklendi',
        description: 'Footer’ı yayınlamak için Tümünü Kaydet düğmesine basın.',
      });
    } catch (requestError) {
      toast({
        title: 'Yükleme başarısız',
        description: requestError.response?.data?.detail || 'Görsel yüklenemedi',
      });
    } finally {
      setUploadingKey(null);
    }
  };

  if (error && !data) return <AdminError message={error} onRetry={load} />;
  if (!data) return <AdminSkeleton rows={8} label="Footer ayarları yükleniyor" />;

  const hiddenCount = countHiddenLinks(data.footer_menu_groups, pages);

  return (
    <div className="pb-20">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(56,189,248,0.15)', color: 'var(--v2-info, #38bdf8)' }}
        >
          <PanelBottom size={18} />
        </div>
        <div>
          <h1 className="text-xl font-bold">Footer Yönetimi</h1>
          <p className="text-xs text-slate-400">Menü sütunlarını, sponsor ve lisans görsellerini yönetin</p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="admin-btn-primary ml-auto flex items-center gap-2"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Tümünü Kaydet
        </button>
      </div>

      <div className="mb-5 rounded-xl border border-cyan-400/15 bg-cyan-400/5 px-4 py-3 flex flex-wrap items-center gap-3">
        <Files size={17} className="text-cyan-400" />
        <div>
          <strong className="block text-sm">Menülerin açtığı sayfa içerikleri</strong>
          <span className="text-xs text-slate-400">Başlık ve metinler ayrı Footer Sayfaları bölümünden yönetilir.</span>
        </div>
        <Link
          to="/admin/footer-yonetimi?sekme=sayfalar"
          className="admin-btn-secondary ml-auto text-xs"
        >
          Footer Sayfalarına Git
        </Link>
      </div>

      {hiddenCount > 0 && (
        <div
          role="alert"
          className="mb-5 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-4 py-3 flex flex-wrap items-center gap-3"
        >
          <EyeOff size={17} className="text-amber-300" />
          <div className="min-w-0 flex-1">
            <strong className="block text-sm text-amber-200">
              {hiddenCount} bağlantı sitede gösterilmiyor
            </strong>
            <span className="text-xs text-slate-400">
              Gittiği footer sayfası yok ya da pasif; bağlantısı kalmayan menü de
              sitede tümüyle gizlenir. Eksik sayfaları Footer Sayfaları →
              Varsayılan İçerikler ile ekleyin ya da sayfayı aktif edin.
            </span>
          </div>
          <Link to="/admin/footer-yonetimi?sekme=sayfalar" className="admin-btn-secondary text-xs">
            Footer Sayfalarını Düzelt
          </Link>
        </div>
      )}

      <CollapsibleSection
        icon={LinkIcon}
        title="Footer Menüleri"
        subtitle="Başlık, ikon, bağlantı ve sıralama buradan yönetilir; işaretlenen menü üst kategori barında da görünür."
        count={data.footer_menu_groups.length}
        open={openSections.menus}
        onToggle={() => toggleSection('menus')}
        action={(
          <button
            type="button"
            onClick={() => setGroups([
              ...data.footer_menu_groups,
              {
                title: 'YENİ MENÜ', icon: 'link', in_topnav: false, in_rail_foot: false, links: [],
              },
            ])}
            className="admin-btn-secondary flex items-center gap-1.5 text-xs shrink-0"
          >
            <Plus size={13} /> Menü Ekle
          </button>
        )}
      >
        <div className="mb-4 rounded-lg border border-cyan-400/15 bg-cyan-400/5 px-3 py-2 text-xs text-slate-400">
          Metinlerde <code className="text-cyan-300">{'{{site_name}}'}</code> kullanabilirsiniz.
          Sitede otomatik olarak <strong className="text-slate-200">{siteName}</strong> gösterilir.
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {data.footer_menu_groups.map((group, groupIndex) => (
            <div key={groupIndex} className="rounded-xl border border-white/[0.07] bg-slate-950/35 p-4">
              <div className="grid grid-cols-[1fr_150px_auto] gap-2 items-end">
                <Field label="Menü Başlığı">
                  <input
                    value={group.title}
                    onChange={(event) => updateGroup(groupIndex, { title: event.target.value })}
                    className="admin-input"
                  />
                </Field>
                <Field label="İkon">
                  <select
                    value={group.icon || 'link'}
                    onChange={(event) => updateGroup(groupIndex, { icon: event.target.value })}
                    className="admin-input"
                  >
                    {iconOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
                <div className="flex gap-1 pb-0.5">
                  <OrderButtons
                    index={groupIndex}
                    count={data.footer_menu_groups.length}
                    onMove={(to) => setGroups(move(data.footer_menu_groups, groupIndex, to))}
                  />
                  <button
                    type="button"
                    onClick={() => setGroups(data.footer_menu_groups.filter((_, index) => index !== groupIndex))}
                    className="icon-btn ban"
                    title="Menüyü sil"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {/* Üst menünün kısayolları ayrı bir yerden değil, işaretlenen
                  footer menüsünden gelir. */}
              <label className="mt-3 flex items-start gap-2 rounded-lg border border-white/[0.07] bg-slate-900/40 px-3 py-2 text-[11px] leading-relaxed text-slate-400">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={!!group.in_topnav}
                  onChange={(event) => updateGroup(groupIndex, { in_topnav: event.target.checked })}
                />
                <span>
                  <strong className="text-slate-200">Üst menüde de göster</strong>
                  {' — '}
                  bu menünün bağlantıları üst kategori barına ve yan menüye,
                  kategorilerin ardına eklenir. Orada her bağlantı bir ikonla
                  çizildiği için aşağıdan ikon seçin.
                </span>
              </label>
              {/* Sol menünün altındaki iletişim kutusu (Beni Ara, Telegram,
                  Canlı Destek) panelden genişletilir: kuruluma özel adresler
                  (canlı yayın bağlantısı gibi) koda gömülmez. */}
              <label className="mt-2 flex items-start gap-2 rounded-lg border border-white/[0.07] bg-slate-900/40 px-3 py-2 text-[11px] leading-relaxed text-slate-400">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={!!group.in_rail_foot}
                  onChange={(event) => updateGroup(groupIndex, { in_rail_foot: event.target.checked })}
                />
                <span>
                  <strong className="text-slate-200">Sol menü altında göster</strong>
                  {' — '}
                  bu menünün bağlantıları sol menünün altındaki iletişim
                  kutusuna (Beni Ara, Telegram, Canlı Destek yanına) eklenir.
                  Yalnızca sol menüsünde iletişim kutusu olan tasarım
                  şemalarında çizilir.
                </span>
              </label>

              <div className="mt-4 space-y-2">
                {(group.links || []).map((item, linkIndex) => {
                  const hiddenReason = hiddenLinkReason(item.url, pages);
                  return (
                  <div key={linkIndex}>
                  <div
                    className={`grid grid-cols-1 gap-2 items-end ${group.in_topnav || group.in_rail_foot
                      ? 'sm:grid-cols-[minmax(110px,0.7fr)_minmax(150px,1fr)_130px_auto]'
                      : 'sm:grid-cols-[minmax(120px,0.7fr)_minmax(160px,1fr)_auto]'}`}
                  >
                    <Field label={linkIndex === 0 ? 'Bağlantı Metni' : undefined}>
                      <input
                        value={item.label}
                        onChange={(event) => updateLink(groupIndex, linkIndex, { label: event.target.value })}
                        className="admin-input"
                        placeholder="Menü metni"
                      />
                    </Field>
                    <Field label={linkIndex === 0 ? 'Bağlantı' : undefined}>
                      <input
                        value={item.url}
                        onChange={(event) => updateLink(groupIndex, linkIndex, { url: event.target.value })}
                        className="admin-input"
                        placeholder="/sayfa veya https://..."
                      />
                    </Field>
                    {/* İkon yalnızca üst menüde çizilir; footer sütununda
                        bağlantılar düz metindir. */}
                    {(group.in_topnav || group.in_rail_foot) && (
                      <Field label={linkIndex === 0 ? 'Menü ikonu' : undefined}>
                        <select
                          value={item.icon || ''}
                          onChange={(event) => updateLink(groupIndex, linkIndex, { icon: event.target.value })}
                          className="admin-input"
                        >
                          <option value="">Bağlantı (varsayılan)</option>
                          {BOTTOM_NAV_ICON_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </Field>
                    )}
                    <div className="flex items-center gap-1 pb-0.5">
                      <label className="inline-flex h-9 items-center gap-1.5 px-2 text-[11px] text-slate-400 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={!!item.new_tab}
                          onChange={(event) => updateLink(groupIndex, linkIndex, { new_tab: event.target.checked })}
                        />
                        Yeni sekme
                      </label>
                      <OrderButtons
                        index={linkIndex}
                        count={(group.links || []).length}
                        onMove={(to) => updateGroup(groupIndex, { links: move(group.links || [], linkIndex, to) })}
                      />
                      <button
                        type="button"
                        onClick={() => updateGroup(groupIndex, {
                          links: group.links.filter((_, index) => index !== linkIndex),
                        })}
                        className="icon-btn ban"
                        title="Bağlantıyı sil"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {hiddenReason && (
                    <p className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-300">
                      <EyeOff size={11} aria-hidden="true" />
                      {HIDDEN_LINK_TEXT[hiddenReason]}
                    </p>
                  )}
                  </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => updateGroup(groupIndex, {
                  links: [
                    ...(group.links || []),
                    { label: 'Yeni Bağlantı', url: '#', new_tab: false, icon: '' },
                  ],
                })}
                className="admin-btn-secondary mt-3 flex items-center gap-1.5 text-xs"
              >
                <Plus size={12} /> Bağlantı Ekle
              </button>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        icon={CreditCard}
        title="Yatırım Yöntemleri"
        subtitle="Footer’ın üstünde logolu rozet olarak gösterilir (mobil ve masaüstü)."
        count={data.footer_payment_methods.length}
        open={openSections.payments}
        onToggle={() => toggleSection('payments')}
        action={(
          <button
            type="button"
            onClick={() => setPayments([
              ...data.footer_payment_methods,
              { label: 'HAVALE', image_url: '', link: '', new_tab: false },
            ])}
            className="admin-btn-secondary flex items-center gap-1.5 text-xs shrink-0"
          >
            <Plus size={13} /> Yöntem Ekle
          </button>
        )}
      >
        <div className="mb-4 max-w-md">
          <Field label="Şerit Başlığı">
            <input
              value={data.footer_payment_title}
              onChange={(event) => setData({ ...data, footer_payment_title: event.target.value })}
              className="admin-input"
              placeholder="YATIRIM YÖNTEMLERİ"
            />
          </Field>
        </div>

        {data.footer_payment_methods.length === 0 ? (
          <p className="text-xs text-slate-500">
            Henüz yöntem eklenmedi — şerit sitede gösterilmez.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {data.footer_payment_methods.map((method, index) => (
              <div key={index} className="rounded-xl border border-white/[0.07] bg-slate-950/35 p-4">
                <div className="h-20 rounded-lg bg-slate-900/80 border border-white/5 flex items-center justify-center mb-3 overflow-hidden">
                  {method.image_url ? (
                    <img
                      src={resolveApiAssetUrl(method.image_url)}
                      alt=""
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <ImageIcon size={24} className="text-slate-600" />
                  )}
                </div>
                <div className="space-y-2">
                  <Field label="Yöntem Adı">
                    <input
                      value={method.label || ''}
                      onChange={(event) => updatePayment(index, { label: event.target.value })}
                      className="admin-input"
                      placeholder="HAVALE, KREDİ KARTI, KRİPTO…"
                    />
                  </Field>
                  <Field label="Logo">
                    <div className="flex gap-2">
                      <input
                        value={method.image_url || ''}
                        onChange={(event) => updatePayment(index, { image_url: event.target.value })}
                        className="admin-input min-w-0"
                        placeholder="https://... veya yüklenen görsel"
                      />
                      <label className="admin-btn-secondary shrink-0 flex items-center gap-1.5 cursor-pointer">
                        {uploadingKey === `footer_payment_methods:${index}`
                          ? <Loader2 size={13} className="animate-spin" />
                          : <Upload size={13} />}
                        Yükle
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                          className="hidden"
                          disabled={uploadingKey !== null}
                          onChange={(event) => {
                            uploadImage('footer_payment_methods', index, event.target.files?.[0]);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </Field>
                  <Field label="Bağlantı (isteğe bağlı)">
                    <input
                      value={method.link || ''}
                      onChange={(event) => updatePayment(index, { link: event.target.value })}
                      className="admin-input"
                      placeholder="/hesap/para-yatir veya https://..."
                    />
                  </Field>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <label className="inline-flex items-center gap-2 text-xs text-slate-400">
                    <input
                      type="checkbox"
                      checked={!!method.new_tab}
                      onChange={(event) => updatePayment(index, { new_tab: event.target.checked })}
                    />
                    Yeni sekmede aç
                  </label>
                  <div className="ml-auto flex gap-1">
                    <OrderButtons
                      index={index}
                      count={data.footer_payment_methods.length}
                      onMove={(to) => setPayments(move(data.footer_payment_methods, index, to))}
                    />
                    <button
                      type="button"
                      onClick={() => setPayments(
                        data.footer_payment_methods.filter((_, itemIndex) => itemIndex !== index),
                      )}
                      className="icon-btn ban"
                      title="Yöntemi sil"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        icon={Handshake}
        title="Sponsorlar"
        subtitle="Yatırım yöntemlerinin hemen altında logo şeridi olarak gösterilir."
        count={data.footer_sponsors.length}
        open={openSections.sponsors}
        onToggle={() => toggleSection('sponsors')}
        action={(
          <button
            type="button"
            onClick={() => setSponsors([
              ...data.footer_sponsors,
              { name: 'Yeni Sponsor', image_url: '', link: '', new_tab: true },
            ])}
            className="admin-btn-secondary flex items-center gap-1.5 text-xs shrink-0"
          >
            <Plus size={13} /> Sponsor Ekle
          </button>
        )}
      >
        <div className="mb-4 max-w-md">
          <Field label="Şerit Başlığı">
            <input
              value={data.footer_sponsor_title}
              onChange={(event) => setData({ ...data, footer_sponsor_title: event.target.value })}
              className="admin-input"
              placeholder="SPONSORLARIMIZ"
            />
          </Field>
        </div>

        {data.footer_sponsors.length === 0 ? (
          <p className="text-xs text-slate-500">
            Henüz sponsor eklenmedi — şerit sitede gösterilmez.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {data.footer_sponsors.map((sponsor, index) => (
              <div key={index} className="rounded-xl border border-white/[0.07] bg-slate-950/35 p-4">
                <div className="h-24 rounded-lg bg-slate-900/80 border border-white/5 flex items-center justify-center mb-3 overflow-hidden">
                  {sponsor.image_url ? (
                    <img
                      src={resolveApiAssetUrl(sponsor.image_url)}
                      alt=""
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <ImageIcon size={28} className="text-slate-600" />
                  )}
                </div>
                <div className="space-y-2">
                  <Field label="Sponsor Adı">
                    <input
                      value={sponsor.name || ''}
                      onChange={(event) => updateSponsor(index, { name: event.target.value })}
                      className="admin-input"
                      placeholder="Takım, turnuva veya marka adı"
                    />
                  </Field>
                  <Field label="Logo">
                    <div className="flex gap-2">
                      <input
                        value={sponsor.image_url || ''}
                        onChange={(event) => updateSponsor(index, { image_url: event.target.value })}
                        className="admin-input min-w-0"
                        placeholder="https://... veya yüklenen görsel"
                      />
                      <label className="admin-btn-secondary shrink-0 flex items-center gap-1.5 cursor-pointer">
                        {uploadingKey === `footer_sponsors:${index}`
                          ? <Loader2 size={13} className="animate-spin" />
                          : <Upload size={13} />}
                        Yükle
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                          className="hidden"
                          disabled={uploadingKey !== null}
                          onChange={(event) => {
                            uploadImage('footer_sponsors', index, event.target.files?.[0]);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </Field>
                  <Field label="Bağlantı (isteğe bağlı)">
                    <input
                      value={sponsor.link || ''}
                      onChange={(event) => updateSponsor(index, { link: event.target.value })}
                      className="admin-input"
                      placeholder="https://..."
                    />
                  </Field>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <label className="inline-flex items-center gap-2 text-xs text-slate-400">
                    <input
                      type="checkbox"
                      checked={!!sponsor.new_tab}
                      onChange={(event) => updateSponsor(index, { new_tab: event.target.checked })}
                    />
                    Yeni sekmede aç
                  </label>
                  <div className="ml-auto flex gap-1">
                    <OrderButtons
                      index={index}
                      count={data.footer_sponsors.length}
                      onMove={(to) => setSponsors(move(data.footer_sponsors, index, to))}
                    />
                    <button
                      type="button"
                      onClick={() => setSponsors(
                        data.footer_sponsors.filter((_, itemIndex) => itemIndex !== index),
                      )}
                      className="icon-btn ban"
                      title="Sponsoru sil"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        icon={BadgeCheck}
        title="Lisanslar"
        subtitle="Görsel URL’si girin veya bilgisayardan yükleyin; ardından bağlantıyı belirleyin."
        count={data.footer_licenses.length}
        open={openSections.licenses}
        onToggle={() => toggleSection('licenses')}
        action={(
          <button
            type="button"
            onClick={() => setLicenses([
              ...data.footer_licenses,
              { name: 'Yeni Lisans', image_url: '', link: '', new_tab: true },
            ])}
            className="admin-btn-secondary flex items-center gap-1.5 text-xs shrink-0"
          >
            <Plus size={13} /> Lisans Ekle
          </button>
        )}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {data.footer_licenses.map((license, index) => (
            <div
              key={index}
              className={`rounded-xl border p-4 ${license.pinned
                ? 'border-amber-400/30 bg-amber-400/[0.04]'
                : 'border-white/[0.07] bg-slate-950/35'}`}
            >
              <div className="h-24 rounded-lg bg-slate-900/80 border border-white/5 flex items-center justify-center mb-3 overflow-hidden">
                {license.image_url ? (
                  <img
                    src={resolveApiAssetUrl(license.image_url)}
                    alt=""
                    className="max-w-full max-h-full object-contain p-2"
                  />
                ) : (
                  <ImageIcon size={28} className="text-slate-600" />
                )}
              </div>
              {/* Sabit lisans koda gömülüdür: alanları salt okunur, silinemez. */}
              {license.pinned && (
                <div className="mb-3 flex items-center gap-1.5 rounded-lg border border-amber-400/25 bg-amber-400/[0.07] px-2.5 py-2 text-[11px] text-amber-300/90">
                  <Lock size={12} className="shrink-0" />
                  Sabit lisans — sistemde tanımlıdır, düzenlenemez ve silinemez.
                </div>
              )}
              <div className="space-y-2">
                <Field label="Lisans Adı">
                  <input
                    value={license.name}
                    onChange={(event) => updateLicense(index, { name: event.target.value })}
                    className="admin-input"
                    readOnly={license.pinned}
                    disabled={license.pinned}
                  />
                </Field>
                <Field label="Görsel URL">
                  <div className="flex gap-2">
                    <input
                      value={license.image_url}
                      onChange={(event) => updateLicense(index, { image_url: event.target.value })}
                      className="admin-input min-w-0"
                      placeholder="https://... veya yüklenen görsel"
                      readOnly={license.pinned}
                      disabled={license.pinned}
                    />
                    {!license.pinned && (
                      <label className="admin-btn-secondary shrink-0 flex items-center gap-1.5 cursor-pointer">
                        {uploadingKey === `footer_licenses:${index}`
                          ? <Loader2 size={13} className="animate-spin" />
                          : <Upload size={13} />}
                        Yükle
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                          className="hidden"
                          disabled={uploadingKey !== null}
                          onChange={(event) => {
                            uploadImage('footer_licenses', index, event.target.files?.[0]);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                </Field>
                <Field label="Bağlantı">
                  <input
                    value={license.link}
                    onChange={(event) => updateLicense(index, { link: event.target.value })}
                    className="admin-input"
                    placeholder="https://..."
                    readOnly={license.pinned}
                    disabled={license.pinned}
                  />
                </Field>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <label className="inline-flex items-center gap-2 text-xs text-slate-400">
                  <input
                    type="checkbox"
                    checked={!!license.new_tab}
                    onChange={(event) => updateLicense(index, { new_tab: event.target.checked })}
                    disabled={license.pinned}
                  />
                  Yeni sekmede aç
                </label>
                {!license.pinned && (
                  <div className="ml-auto flex gap-1">
                    <OrderButtons
                      index={index}
                      count={data.footer_licenses.length}
                      onMove={(to) => setLicenses(move(data.footer_licenses, index, to))}
                    />
                    <button
                      type="button"
                      onClick={() => setLicenses(data.footer_licenses.filter((_, itemIndex) => itemIndex !== index))}
                      className="icon-btn ban"
                      title="Lisansı sil"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5">
          <Field label="Lisans Açıklama Metni">
            <textarea
              value={data.footer_license_text}
              onChange={(event) => setData({ ...data, footer_license_text: event.target.value })}
              className="admin-input"
              style={{ minHeight: 96, padding: 12 }}
              placeholder="Lisans görsellerinin altında gösterilecek açıklama..."
            />
          </Field>
        </div>

        <div className="mt-5">
          <Field
            label="Telif Başlangıç Yılı"
            hint="Footer'ın en altındaki telif satırı bu yıldan bugüne uzanır (2021 - 2026 Marka). Boş bırakılırsa yalnızca içinde bulunulan yıl yazılır."
          >
            <input
              type="number"
              min="1990"
              max="2999"
              value={data.footer_start_year}
              onChange={(event) => setData({ ...data, footer_start_year: event.target.value })}
              className="admin-input admin-input-w-sm"
              placeholder="2021"
            />
          </Field>
        </div>
      </CollapsibleSection>
    </div>
  );
};

// Başlık satırı her zaman görünür, gövde katlanır: sayfa üç bölümün tamamı
// açıkken çok uzuyordu.
const CollapsibleSection = ({
  icon: Icon, title, subtitle, count, open, onToggle, action, children,
}) => (
  <section className="admin-card mb-5">
    <div className="flex items-center gap-3 p-4 md:p-5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-500 transition-transform ${open ? '' : '-rotate-90'}`}
        />
        <Icon size={16} className="shrink-0 text-cyan-400" />
        <span className="min-w-0">
          <span className="block font-bold">{title}</span>
          <span className="block text-xs text-slate-500">{subtitle}</span>
        </span>
        <span className="ml-2 shrink-0 rounded-full bg-slate-800/70 px-2 py-0.5 text-[11px] text-slate-400">
          {count}
        </span>
      </button>
      {open && action}
    </div>
    {open && <div className="px-4 pb-5 md:px-5 md:pb-6">{children}</div>}
  </section>
);

const Field = ({ label, hint, children }) => (
  <div>
    {label && <label className="text-[11px] text-slate-500 block mb-1">{label}</label>}
    {children}
    {hint && <p className="mt-1 text-[11px] leading-4 text-slate-500">{hint}</p>}
  </div>
);

const OrderButtons = ({
  index, count, onMove,
}) => (
  <>
    <button
      type="button"
      onClick={() => onMove(index - 1)}
      disabled={index === 0}
      className="icon-btn view disabled:opacity-30"
      title="Yukarı taşı"
    >
      <ArrowUp size={12} />
    </button>
    <button
      type="button"
      onClick={() => onMove(index + 1)}
      disabled={index === count - 1}
      className="icon-btn view disabled:opacity-30"
      title="Aşağı taşı"
    >
      <ArrowDown size={12} />
    </button>
  </>
);

export default FooterManagement;
